// ФАЙЛ: OverlayEffectSlot.tsx
// НАЗНАЧЕНИЕ: Общий слой позиционирования наслаивающихся эффектов
// (custom_effects, donation_alert, fireworks, text_alert). В отличие от
// zone-layout (widgets/zone-layout, shared/lib/zone-layout) — здесь НЕТ
// стекирования и НЕТ влияния на layout других виджетов: каждый эффект
// получает фиксированную CSS-позицию по своей зоне и рендерится независимо,
// эффекты одной зоны могут визуально перекрываться (ожидаемо для алертов).
//
// Поддерживает 8 именованных зон zone-layout (для единообразия конфигурации
// на бэкенде) плюс два спецрежима: 'fullscreen' (весь экран) и 'center'
// (крупный центральный блок) — они не являются частью 8-зонной сетки, так как
// не привязаны к краю экрана.

import type { ZoneId } from '@shared/lib/zone-layout'
import type { CSSProperties, ReactNode } from 'react'

import styles from './OverlayEffectSlot.module.css'

/** Спецрежимы позиционирования, не входящие в 8 именованных зон. */
export type OverlayEffectSlotMode = 'fullscreen' | 'center'

/** Полный набор допустимых значений zone для наслаивающихся эффектов. */
export type OverlayEffectSlotZone = ZoneId | OverlayEffectSlotMode

const ZONE_CLASS_NAME: Record<OverlayEffectSlotZone, keyof typeof styles> = {
  fullscreen: 'fullscreen',
  center: 'center',
  'top-left': 'topLeft',
  'top-center': 'topCenter',
  'top-right': 'topRight',
  'middle-left': 'middleLeft',
  'middle-right': 'middleRight',
  'bottom-left': 'bottomLeft',
  'bottom-center': 'bottomCenter',
  'bottom-right': 'bottomRight',
}

/** Проверка, что произвольное значение — валидная зона слота эффекта. */
export function isOverlayEffectSlotZone(value: unknown): value is OverlayEffectSlotZone {
  return typeof value === 'string' && value in ZONE_CLASS_NAME
}

interface OverlayEffectSlotProps {
  /** Зона позиционирования; невалидное/отсутствующее значение — 'fullscreen'. */
  zone: OverlayEffectSlotZone | string | undefined
  /** Значение по умолчанию, если zone не задана или невалидна. */
  defaultZone?: OverlayEffectSlotZone
  /**
   * Ограничение допустимых зон для конкретного плагина — не все типы эффектов
   * обязаны поддерживать все 8+fullscreen зон (напр. старый UI выбора зоны на
   * бэкенде может знать только о подмножестве). Значение вне списка трактуется как
   * невалидное и заменяется на defaultZone — так же, как и отсутствие значения.
   */
  allowedZones?: readonly OverlayEffectSlotZone[]
  /**
   * Собственная fade-in анимация слота (opacity+scale, 300мс). Отключается (false)
   * для эффектов, у которых уже есть своя WAAPI-анимация входа/выхода (donation_alert,
   * text_alert) — иначе получился бы двойной переход (слот fade-in + собственный slide-in).
   */
  animateIn?: boolean
  children: ReactNode
  /** data-атрибут для отладки/тестов — какая зона реально применена. */
  'data-testid'?: string
  style?: CSSProperties
}

/**
 * Слот позиционирования одного наслаивающегося эффекта. Не хранит состояние и
 * не координирует несколько эффектов между собой — в отличие от
 * ZoneLayoutManager, здесь каждый вызов независим (просто CSS-класс по зоне).
 */
export function OverlayEffectSlot({
  zone,
  defaultZone = 'fullscreen',
  allowedZones,
  animateIn = true,
  children,
  style,
  ...rest
}: OverlayEffectSlotProps) {
  const isAllowed = (z: OverlayEffectSlotZone): boolean =>
    !allowedZones || allowedZones.includes(z)
  const resolvedZone =
    isOverlayEffectSlotZone(zone) && isAllowed(zone) ? zone : defaultZone
  const className = [
    styles.root,
    styles[ZONE_CLASS_NAME[resolvedZone]],
    animateIn ? styles.animateIn : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} data-zone={resolvedZone} style={style} {...rest}>
      <div className={styles.content}>{children}</div>
    </div>
  )
}
